import axios from 'axios';

// Configure axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://api-beta-open-tender.nat-quest.com' 
      : 'http://localhost:2024'),
  timeout: 60000, // 1 minute timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_STORAGE_KEY = 'opentender_tokens';

const getAccessToken = (): string | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const { accessToken } = JSON.parse(stored);
    return accessToken;
  } catch {
    return null;
  }
};

const getRefreshToken = (): string | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const { refreshToken } = JSON.parse(stored);
    return refreshToken;
  } catch {
    return null;
  }
};

const updateAccessToken = (newAccessToken: string): void => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const tokenData = JSON.parse(stored);
      tokenData.accessToken = newAccessToken;
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    }
  } catch {
    // Ignore errors
  }
};

// Request interceptor to add authentication
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          // Attempt to refresh the token
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL || 
              (process.env.NODE_ENV === 'production' 
                ? 'https://api-beta-open-tender.nat-quest.com' 
                : 'http://localhost:2024')}/auth/refresh`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${refreshToken}`,
                'Content-Type': 'application/json',
              },
            }
          );
          
          const { access_token } = refreshResponse.data;
          updateAccessToken(access_token);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Types
export interface ProjectCreateData {
  title: string;           // Maps to "name" on backend
  description?: string;
  industry: string;
  priority: 'low' | 'medium' | 'high';
  estimatedValue?: string; // Maps to "estimated_value" on backend
  deadline?: string;       // Will be converted to Date on backend
  tags?: string[];
}

export interface ProjectData {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  industry: string;
  priority: string;
  estimated_value?: string;
  deadline?: string;
  company_id?: string;
  status: string;
  created_at: string;
  updated_at: string;
  tags: string[];
  metadata?: any;
}

export interface ProjectListResponse {
  projects: ProjectData[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface ProjectUpdateData {
  name?: string;
  description?: string;
  industry?: string;
  priority?: 'low' | 'medium' | 'high';
  estimated_value?: string;
  deadline?: string;
  status?: string;
  tags?: string[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  message?: string;
  error?: string;
}

/**
 * Project API service
 */
export class ProjectApi {
  /**
   * Create a new project
   */
  static async createProject(projectData: ProjectCreateData): Promise<ApiResponse<ProjectData>> {
    try {
      // Map frontend field names to backend field names
      const backendData = {
        name: projectData.title,
        description: projectData.description,
        industry: projectData.industry,
        priority: projectData.priority,
        estimated_value: projectData.estimatedValue,
        deadline: projectData.deadline ? new Date(projectData.deadline).toISOString() : null,
        tags: projectData.tags || [],
        // company_id will be auto-set by backend from authenticated user
      };

      const response = await api.post('/api/projects', backendData);
      
      return {
        success: true,
        data: response.data,
        message: 'Project created successfully'
      };
    } catch (error: any) {
      console.error('Create project error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.detail || 'Failed to create project'
      };
    }
  }

  /**
   * Get all projects for the current user
   */
  static async getProjects(page: number = 1, pageSize: number = 20, statusFilter?: string): Promise<ApiResponse<ProjectListResponse>> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (statusFilter) {
        params.append('status_filter', statusFilter);
      }

      const response = await api.get(`/api/projects?${params.toString()}`);
      
      return {
        success: true,
        data: response.data,
        message: 'Projects retrieved successfully'
      };
    } catch (error: any) {
      console.error('Get projects error:', error);
      return {
        success: false,
        data: { projects: [], total: 0, page: 1, page_size: 20, has_more: false },
        error: error.response?.data?.detail || 'Failed to retrieve projects'
      };
    }
  }

  /**
   * Get a single project by ID
   */
  static async getProject(projectId: string): Promise<ApiResponse<ProjectData>> {
    try {
      const response = await api.get(`/api/projects/${projectId}`);
      
      return {
        success: true,
        data: response.data,
        message: 'Project retrieved successfully'
      };
    } catch (error: any) {
      console.error('Get project error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.detail || 'Failed to retrieve project'
      };
    }
  }

  /**
   * Update a project
   */
  static async updateProject(projectId: string, updateData: ProjectUpdateData): Promise<ApiResponse<ProjectData>> {
    try {
      // Map frontend field names to backend field names if needed
      const backendData = {
        ...updateData,
        deadline: updateData.deadline ? new Date(updateData.deadline).toISOString() : undefined,
      };

      const response = await api.put(`/api/projects/${projectId}`, backendData);
      
      return {
        success: true,
        data: response.data,
        message: 'Project updated successfully'
      };
    } catch (error: any) {
      console.error('Update project error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.detail || 'Failed to update project'
      };
    }
  }

  /**
   * Delete a project
   */
  static async deleteProject(projectId: string): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/api/projects/${projectId}`);
      
      return {
        success: true,
        data: undefined,
        message: 'Project deleted successfully'
      };
    } catch (error: any) {
      console.error('Delete project error:', error);
      return {
        success: false,
        data: undefined,
        error: error.response?.data?.detail || 'Failed to delete project'
      };
    }
  }

  /**
   * Archive a project
   */
  static async archiveProject(projectId: string): Promise<ApiResponse<void>> {
    try {
      await api.post(`/api/projects/${projectId}/archive`);
      
      return {
        success: true,
        data: undefined,
        message: 'Project archived successfully'
      };
    } catch (error: any) {
      console.error('Archive project error:', error);
      return {
        success: false,
        data: undefined,
        error: error.response?.data?.detail || 'Failed to archive project'
      };
    }
  }

  /**
   * Restore an archived project
   */
  static async restoreProject(projectId: string): Promise<ApiResponse<void>> {
    try {
      await api.post(`/api/projects/${projectId}/restore`);
      
      return {
        success: true,
        data: undefined,
        message: 'Project restored successfully'
      };
    } catch (error: any) {
      console.error('Restore project error:', error);
      return {
        success: false,
        data: undefined,
        error: error.response?.data?.detail || 'Failed to restore project'
      };
    }
  }
}

export default ProjectApi;